<template>
  <div class="profile-question-card">
    <div class="profile-question-card__header d-flex flex-wrap align-items-center justify-content-between">
      <div class="d-flex align-items-center flex-wrap">
        <span class="profile-question-card__badge">Q{{ index + 1 }}</span>
        <span class="profile-question-card__type-label">{{ typeLabel }}</span>
        <span
          v-if="validationMessage"
          class="profile-question-card__alert-badge"
          :title="validationMessage"
        >
          <i class="fas fa-exclamation-triangle" />
        </span>
      </div>
      <div class="d-flex flex-wrap">
        <button
          type="button"
          class="btn btn-sm btn-alt-secondary profile-question-card__icon-btn"
          aria-label="Duplicate question"
          title="Duplicate question"
          @click="$emit('duplicate')"
        >
          <i class="far fa-clone" />
        </button>
        <button
          type="button"
          class="btn btn-sm btn-alt-danger profile-question-card__icon-btn"
          aria-label="Remove question"
          title="Remove question"
          @click="$emit('remove')"
        >
          <i class="fa fa-times" />
        </button>
      </div>
    </div>

    <div class="profile-question-card__body">
      <div
        class="btn-group profile-question-card__type-group"
        role="group"
        aria-label="Question type"
      >
        <button
          v-for="option in typeOptions"
          :key="option.value"
          type="button"
          class="btn btn-sm profile-question-card__type-btn"
          :class="localQuestion.type === option.value ? 'profile-question-card__type-btn--active' : ''"
          :aria-pressed="localQuestion.type === option.value"
          @click="setType(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <div class="form-group mb-0">
        <label
          class="sr-only"
          :for="questionInputId"
        >Question {{ index + 1 }}</label>
        <textarea
          :id="questionInputId"
          v-model="localQuestion.question"
          class="form-control"
          name="question[]"
          rows="3"
          placeholder="Ask experts a screening question"
          @input="emitChange"
        />
      </div>

      <div
        v-if="localQuestion.type === 'text'"
        class="profile-question-card__caption"
      >
        <i class="far fa-keyboard me-1" />
        Expert answers with free text.
      </div>

      <div v-else>
        <div class="profile-question-card__caption">
          Options — expert picks {{ localQuestion.type === 'multiple' ? 'several' : 'one' }}
        </div>

        <div
          v-for="(option, optionIndex) in localQuestion.options"
          :key="option.id"
          class="profile-question-card__option-row d-flex flex-wrap align-items-center"
        >
          <i
            class="profile-question-card__option-glyph"
            :class="localQuestion.type === 'multiple' ? 'far fa-square' : 'far fa-circle'"
            aria-hidden="true"
          />
          <label
            class="sr-only"
            :for="optionInputId(optionIndex)"
          >Option {{ optionIndex + 1 }}</label>
          <input
            :id="optionInputId(optionIndex)"
            v-model="option.text"
            type="text"
            class="form-control profile-question-card__option-input"
            maxlength="255"
            :placeholder="`Option ${optionIndex + 1}`"
            @input="emitChange"
            @keydown.enter.prevent="addOptionAfter(optionIndex)"
          >
          <button
            type="button"
            class="btn btn-sm btn-alt-danger profile-question-card__icon-btn"
            aria-label="Remove option"
            title="Remove option"
            @click="removeOption(optionIndex)"
          >
            <i class="fa fa-times" />
          </button>
        </div>

        <button
          type="button"
          class="btn btn-sm btn-alt-secondary profile-question-card__add-option"
          @click="addOption()"
        >
          <i class="fa fa-plus me-1" />
          Add option
        </button>
        <span class="profile-question-card__hint">Press Enter to add the next one</span>

        <div
          v-if="validationMessage"
          class="profile-question-card__validation text-danger"
          role="alert"
        >
          <i class="fa fa-exclamation-circle me-1" />
          {{ validationMessage }}
        </div>

        <div class="profile-question-card__description">
          <div class="custom-control custom-switch">
            <input
              :id="descriptionToggleId"
              v-model="localQuestion.descriptionEnabled"
              type="checkbox"
              class="custom-control-input"
              @change="emitChange"
            >
            <label
              class="custom-control-label"
              :for="descriptionToggleId"
            >Ask for an additional written explanation</label>
          </div>

          <div
            v-if="localQuestion.descriptionEnabled"
            class="form-group mt-2 mb-0"
          >
            <label
              class="sr-only"
              :for="descriptionLabelId"
            >Explanation label</label>
            <input
              :id="descriptionLabelId"
              v-model="localQuestion.descriptionLabel"
              type="text"
              class="form-control"
              maxlength="255"
              :placeholder="defaultDescriptionLabel"
              @input="emitChange"
            >
            <small class="profile-question-card__hint">
              Shown above the explanation textarea. Leave blank to use the default.
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden inputs keep the submitted payload byte-for-byte identical to today -->
    <input
      type="hidden"
      name="projectId[]"
      :value="localQuestion.id"
    >
    <input
      type="hidden"
      name="question_type[]"
      :value="localQuestion.type"
    >
    <input
      type="hidden"
      name="question_options[]"
      :value="localQuestion.type === 'text' ? '[]' : JSON.stringify(nonEmptyOptionTexts)"
    >
    <!-- Hidden inputs carry the toggle: an unchecked checkbox submits nothing, which
         would break the parallel-array indexing the controller relies on. -->
    <input
      type="hidden"
      name="question_description_enabled[]"
      :value="localQuestion.type === 'text' || !localQuestion.descriptionEnabled ? '0' : '1'"
    >
    <input
      type="hidden"
      name="question_description_label[]"
      :value="localQuestion.type === 'text' ? '' : (localQuestion.descriptionLabel || '')"
    >
  </div>
</template>

<script>
import { DEFAULT_DESCRIPTION_LABEL } from "./profileQuestionDefaults";

export default {
    name: "ProfileQuestionCard",
    props: {
        question: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        total: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            localQuestion: this.cloneQuestion(this.question),
            defaultDescriptionLabel: DEFAULT_DESCRIPTION_LABEL,
            typeOptions: [
                { value: "text", label: "Free Text" },
                { value: "single", label: "Single Choice" },
                { value: "multiple", label: "Multiple Choice" },
            ],
        };
    },
    computed: {
        typeLabel() {
            const match = this.typeOptions.find(opt => opt.value === this.localQuestion.type);
            return match ? match.label : "Free Text";
        },
        questionInputId() {
            return `profile-question-${this.localQuestion.id}`;
        },
        descriptionToggleId() {
            return `profile-question-${this.localQuestion.id}-description-enabled`;
        },
        descriptionLabelId() {
            return `profile-question-${this.localQuestion.id}-description-label`;
        },
        nonEmptyOptionTexts() {
            return (this.localQuestion.options || [])
                .map(option => ({ ...option, text: (option.text || "").trim() }))
                .filter(option => option.text.length > 0)
                .map(option => option.text);
        },
        validationMessage() {
            if (this.localQuestion.type === "text") {
                return "";
            }

            const nonEmpty = (this.localQuestion.options || [])
                .map(option => (option.text || "").trim())
                .filter(text => text.length > 0);

            if (nonEmpty.length < 2) {
                return "Add at least 2 non-empty options.";
            }

            const seen = new Set();
            for (const text of nonEmpty) {
                const key = text.toLowerCase();
                if (seen.has(key)) {
                    return "Duplicate options are not allowed.";
                }
                seen.add(key);
            }

            return "";
        },
    },
    watch: {
        question: {
            handler(newQuestion) {
                if (newQuestion === this.localQuestion) {
                    return;
                }
                this.localQuestion = this.cloneQuestion(newQuestion);
            },
            deep: true,
        },
    },
    methods: {
        cloneQuestion(question) {
            return {
                id: question.id,
                type: question.type || "text",
                question: question.question || "",
                options: (question.options || []).map(option => ({ id: option.id, text: option.text })),
                descriptionEnabled: question.descriptionEnabled !== false,
                descriptionLabel: question.descriptionLabel || "",
            };
        },
        optionInputId(optionIndex) {
            return `profile-question-${this.localQuestion.id}-option-${optionIndex}`;
        },
        setType(newType) {
            if (this.localQuestion.type === newType) {
                return;
            }

            this.localQuestion.type = newType;

            const options = this.localQuestion.options || [];

            if (newType !== "text" && options.filter(o => (o.text || "").trim()).length < 2) {
                const blanksNeeded = 2 - options.length;
                for (let i = 0; i < Math.max(0, blanksNeeded); i += 1) {
                    this.localQuestion.options.push({ id: btoa(_.uniqueId("option_")), text: "" });
                }
            }

            this.emitChange();
        },
        addOption() {
            this.localQuestion.options.push({ id: btoa(_.uniqueId("option_")), text: "" });
            this.emitChange();
            this.focusLastOption();
        },
        addOptionAfter(optionIndex) {
            this.localQuestion.options.splice(optionIndex + 1, 0, {
                id: btoa(_.uniqueId("option_")),
                text: "",
            });
            this.emitChange();
            this.focusOption(optionIndex + 1);
        },
        removeOption(optionIndex) {
            this.localQuestion.options.splice(optionIndex, 1);
            this.emitChange();
        },
        focusLastOption() {
            this.focusOption(this.localQuestion.options.length - 1);
        },
        focusOption(optionIndex) {
            this.$nextTick(() => {
                const id = this.optionInputId(optionIndex);
                const el = document.getElementById(id);
                if (el) {
                    el.focus();
                }
            });
        },
        emitChange() {
            this.$emit("input", this.localQuestion);
        },
        focusQuestion() {
            this.$nextTick(() => {
                const el = document.getElementById(this.questionInputId);
                if (el) {
                    el.focus();
                }
            });
        },
    },
};
</script>

<style scoped>
.profile-question-card {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #fff;
}

.profile-question-card__header {
    padding: 0.6rem 1rem;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    gap: 0.5rem;
}

.profile-question-card__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.75rem;
    height: 1.5rem;
    padding: 0 0.4rem;
    border-radius: 0.35rem;
    background: #5c80d1;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    margin-right: 0.5rem;
}

.profile-question-card__type-label {
    font-weight: 600;
    color: #2f3542;
}

.profile-question-card__alert-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    margin-left: 0.5rem;
    color: #d9534f;
    font-size: 0.8rem;
}

.profile-question-card__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.35rem;
}

.profile-question-card__body {
    padding: 1rem;
}

.profile-question-card__type-group {
    display: inline-flex;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.profile-question-card__type-btn {
    border: 1px solid #e9ecef;
    background: #fff;
    color: #2f3542;
    cursor: pointer;
    transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.profile-question-card__type-btn:not(:last-child) {
    border-right: 0;
}

.profile-question-card__type-btn--active,
.profile-question-card__type-btn--active:hover {
    background: #5c80d1;
    border-color: #5c80d1;
    color: #fff;
}

.profile-question-card__type-btn:focus-visible {
    outline: 2px solid #5c80d1;
    outline-offset: -2px;
}

.profile-question-card__caption {
    margin-top: 0.5rem;
    color: #6c757d;
    font-size: 0.85rem;
}

.profile-question-card__option-row {
    margin-bottom: 0.4rem;
    gap: 0.4rem;
}

.profile-question-card__option-glyph {
    color: #6c757d;
    font-size: 1rem;
    width: 1.1rem;
    text-align: center;
    flex-shrink: 0;
}

.profile-question-card__option-input {
    flex: 1 1 12rem;
    min-width: 8rem;
}

.profile-question-card__add-option {
    margin-right: 0.5rem;
    cursor: pointer;
}

.profile-question-card__hint {
    color: #6c757d;
    font-size: 0.8rem;
}

.profile-question-card__validation {
    margin-top: 0.5rem;
    font-size: 0.85rem;
}

.profile-question-card__description {
    margin-top: 0.85rem;
    padding-top: 0.85rem;
    border-top: 1px dashed #e9ecef;
}

.profile-question-card__icon-btn:focus-visible,
.profile-question-card__add-option:focus-visible {
    outline: 2px solid #5c80d1;
    outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
    .profile-question-card,
    .profile-question-card__type-btn {
        transition: none;
    }
}
</style>
